// @ts-nocheck
import axios, { isCancel } from 'axios';

import SwalToast from '../components/Common/swalToast';
import API_ENDPOINTS from '../const/ApiEndPoints';
import { decryptResponseData, encryptJsonString } from './DualEncryption';
import { isCommonAuthEnabled } from '../const/commonAuthConfig';
import {
  clearStorageWithPreservation,
  clearCommonAuthData,
  redirectToLogout,
} from './commonAuthUtils';
import { isValueEmpty } from './Utils';
import { SESSION_EVENTS } from '../const/CommonConst';
import {
  getUpdatedAccessToken,
  notifySessionRefreshed,
  waitForSessionEvent,
  withSessionRefreshLock,
} from './sessionSync';
import { notifySessionLogout } from './sessionBroadcast';
import {
  claimSessionPrompt,
  clearIdleReturn,
  hasActiveForeignClaim,
  holdPromptForLogout,
  isInactivityPromptEnabled,
  isUserInactive,
  releasePromptClaim,
  shouldPromptBecauseIdle,
  startPromptHeartbeat,
  startUserActivityTracking,
  stopPromptHeartbeat,
} from './userActivity';

const EXCLUDED_PATHS = [
  '/auth/login',
  '/token/refresh/',
  'get_details_for_sso',
  '/access-management/refresh/',
];
// These variables belong to this tab. The flag covers the user prompt as well
// as the refresh request; the Web Lock below coordinates network calls across tabs.
let isRefreshing = false;
// Only failed authenticated requests are queued; normal requests remain usable.
let failedQueue = [];

/**
 * Checks if a given URL path is excluded from processing.
 *
 * @param {string} url - The URL to check.
 * @return {boolean} True if the URL path is excluded, false otherwise.
 */
const isPathExcluded = (url) => {
  const pathname = new URL(url)?.pathname;
  return EXCLUDED_PATHS.some((path) => pathname?.includes(path));
};

/**
 * Processes the queued requests once the token refresh is completed.
 *
 * @param {Error|null} error - Reject all waiters on failure; null on recovery.
 * @param {string|null} [token=null] - The shared access token after recovery.
 * @returns {void}
 */
const processQueue = (error, token = null) => {
  // Iterate over the failedQueue array
  failedQueue.forEach((prom) => {
    if (error) {
      // If there was an error refreshing the token, reject the promise
      prom.reject(error);
    } else {
      // If the token refresh was successful, resolve the promise with the new token
      prom.resolve(token);
    }
  });

  // Clear the failedQueue array after processing
  failedQueue = [];
};

/**
 * Refreshes the access token by making a POST request to the refresh token endpoint.
 *
 * @param {string} refreshToken - The refresh token used to refresh the access token.
 * @returns {Promise<import('axios').AxiosResponse>} The decrypted refresh response.
 */
const refreshAccessToken = (refreshToken) => {
  const organization = localStorage.getItem('REALM_ID');
  const refreshTokenPath = isCommonAuthEnabled()
    ? API_ENDPOINTS.AUTH.REFRESH_TOKEN_COMMON_AUTH
    : API_ENDPOINTS.AUTH.REFRESH_TOKEN;

  const payload = isCommonAuthEnabled()
    ? {
        refresh_token: refreshToken,
        session_id: localStorage.getItem('session_id'),
      }
    : {
        refresh: refreshToken,
      };

  return axios.post(
    `${API_ENDPOINTS.MODULE_BASE_URL.AUTH + refreshTokenPath}`,
    {
      ...payload,
    },
    {
      headers: {
        org: organization,
      },
    }
  );
};

/**
 * Logs out the user using common auth system.
 * Uses the same logic as the useCommonAuth hook but adapted for utility context.
 *
 * @return {Promise} A Promise that resolves with the response from the logout endpoint.
 * @throws {Error} If there is an error during the logout process.
 */
const logoutCommonAuth = async () => {
  try {
    const accessToken = localStorage.getItem('access_token');
    const sessionId = localStorage.getItem('session_id');

    const logoutConfig = {
      // An expired logout request must finish cleanup, not enter session recovery.
      _isLogoutRequest: true,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };
    if (isValueEmpty(sessionId)) {
      clearCommonAuthData();
      notifySessionLogout();
      redirectToLogout();
      return;
    }
    // Call the common auth logout API
    await axios
      .post(
        `${API_ENDPOINTS.MODULE_BASE_URL.AUTH}${API_ENDPOINTS.AUTH.LOGOUT_USER_COMMON_AUTH}`,
        { session_id: sessionId },
        logoutConfig
      )
      .then((res) => {
        return res;
      });

    // Clear all auth-related data using centralized utility
    clearCommonAuthData();
    notifySessionLogout();

    SwalToast({
      icon: 'success',
      title: 'Successfully logged out',
    });

    // Redirect directly to logout URL
    redirectToLogout();
  } catch (_error) {
    // Even if API call fails, ensure we clear local data and redirect
    clearCommonAuthData();
    notifySessionLogout();

    // Still redirect to logout even on error
    redirectToLogout();
  }
};

/**
 * Logs out the user using regular auth system.
 *
 * @return {Promise} A Promise that resolves with the response from the logout endpoint.
 * @throws {Error} If there is an error during the logout process.
 */
const logoutRegularAuth = () => {
  const refreshToken = localStorage.getItem('refresh_token');
  const accessToken = localStorage.getItem('access_token');
  const userData = localStorage.getItem('user_data');
  const username = userData ? JSON.parse(userData)?.username : null;

  const logoutConfig = {
    _isLogoutRequest: true,
    headers: {},
  };

  return axios
    .post(
      `${API_ENDPOINTS.MODULE_BASE_URL.AUTH}${API_ENDPOINTS.AUTH.LOGOUT_USER}`,
      {
        refresh_token: refreshToken,
        access_token: accessToken,
        username,
      },
      logoutConfig
    )
    .then((res) => {
      SwalToast({
        icon: 'success',
        title: 'User logged out successfully.',
      });
      return res;
    })
    .catch((err) => {
      throw err;
    })
    .finally(() => {
      // Use centralized storage clearing utility
      clearStorageWithPreservation();
      notifySessionLogout();

      window.location.reload();
    });
};

/**
 * Logs out the user by determining the auth type and calling the appropriate logout function.
 *
 * @return {Promise} A Promise that resolves with the response from the logout endpoint.
 * @throws {Error} If there is an error during the logout process.
 */
const logout = () => {
  if (isCommonAuthEnabled()) {
    return logoutCommonAuth();
  }
  return logoutRegularAuth();
};

/**
 * Encrypts the request parameters or data if encryption is enabled and conditions are met.
 *
 * @param {import('axios').InternalAxiosRequestConfig} request - The request configuration.
 * @returns {Object} - The modified axios request configuration object with encrypted data if applicable.
 */
function handleRequestEncryption(request) {
  const req = { ...request };
  // Replayed requests retain this marker so their existing payload is not encrypted twice.
  if (window.CONFIG.ENABLE_ENCRYPTION && !req?._alreadyEncrypted) {
    // If request parameters exist, encrypt them
    if (req?.params) {
      const { data, encryptedKey } = encryptJsonString(
        JSON.stringify(req?.params)
      );
      // Replace the parameters with the encrypted data
      req.params = { data, encrypted_key: encryptedKey };
      // Mark the request as already encrypted
      req._alreadyEncrypted = true;
    }

    // Handle encryption for multipart/form-data content-type
    if (req?.data && req?.headers?.['Content-Type'] === 'multipart/form-data') {
      const { data, encryptedKey } = encryptJsonString(req?.data?.data);
      // Encrypt only the data part, leaving file uploads intact
      const updatedData = {
        ...req?.data,
        data,
        encrypted_key: encryptedKey,
      };
      req.data = updatedData; // Merge the encrypted data back with the other form-data fields
      req._alreadyEncrypted = true; // Mark the request as already encrypted
    }

    // Handle encryption for JSON content-type
    if (req?.data && req?.headers?.['Content-Type'] === 'application/json') {
      // Encrypt the data after converting it to a JSON string
      const { data, encryptedKey } = encryptJsonString(
        JSON.stringify(req?.data)
      );
      req.data = { data, encrypted_key: encryptedKey }; // Replace the request data with the encrypted data
      req._alreadyEncrypted = true; // Mark the request as already encrypted
    }
  }

  return req; // Return the modified request object
}

/**
 * Attaches the Authorization and organization headers to the request if applicable.
 *
 * @param {import('axios').InternalAxiosRequestConfig} request - The request configuration.
 * @param {string|null} token - The current access token read from shared storage.
 * @returns {Object} - The modified axios request configuration object with attached headers.
 */
const attachAuthHeaders = (request, token) => {
  const req = { ...request };
  const isProtectedApiRequest =
    API_ENDPOINTS.HOSTS_NEEDS_TOKEN.includes(
      new URL(req.url).host.split(':')[0]
    ) && !isPathExcluded(req.url);

  if (isProtectedApiRequest) {
    // Mark protected API requests even when access_token is missing. A 401 from
    // such a request can still recover with a valid refresh_token. Login, SSO
    // and external-host requests remain outside the session-refresh flow.
    req._isProtectedApiRequest = true;
    // Snapshot the token actually attached to this attempt. A delayed 401 can
    // then be distinguished from a rejection of the current shared token.
    // This is Axios metadata, not an extra HTTP header or request-body field.
    req._authToken = token;
    if (token) {
      // Use Bearer token for common auth, JWT for regular auth.
      if (isCommonAuthEnabled()) {
        req.headers.authorization = `Bearer ${token}`;
      } else {
        req.headers.authorization = `JWT ${token}`;
      }
    }
  }
  if (
    API_ENDPOINTS.HOSTS_NEEDS_TOKEN.includes(
      new URL(req.url).host.split(':')[0]
    )
  ) {
    // For regular and common auth we are keeping the org in REALM_ID key in the local storage so no need for profile_data.
    req.headers.org = localStorage.getItem('REALM_ID');
  }

  return req;
};

/**
 * Read the rotated token pair from either auth response shape.
 * Common auth uses access_token/refresh_token. Vault auth uses access/refresh.
 * A previous version accepted the vault shape even when common auth was enabled;
 * requiring only one shape dropped a successful refresh and logged the user out.
 * @param {Object} data - Refresh response body.
 * @returns {{access: string|undefined, refresh: string|undefined}}
 */
const readRefreshedTokens = (data) => ({
  access: data?.access_token || data?.access,
  refresh: data?.refresh_token || data?.refresh,
});

/**
 * Read a JWT exp claim in milliseconds. Returns null for opaque tokens.
 * @param {string|null} token - Access token from storage.
 * @returns {number|null}
 */
const getAccessTokenExpiry = (token) => {
  const payload = token?.split?.('.')?.[1];
  if (!payload) return null;
  try {
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      '='
    );
    const decoded = JSON.parse(atob(padded));
    return typeof decoded?.exp === 'number' ? decoded.exp * 1000 : null;
  } catch (_error) {
    return null;
  }
};

/**
 * Only a JWT exp already in the past is treated as expired here. A missing
 * refresh token can be a brief gap while another login flow rewrites storage,
 * so it is not treated as expiry. Opaque tokens have no exp.
 * @param {string|null} refreshToken - Refresh token captured for this 401.
 * @returns {boolean}
 */
const isRefreshTokenExpired = (refreshToken) => {
  if (!refreshToken) return false;
  const expiresAt = getAccessTokenExpiry(refreshToken);
  if (!expiresAt) return false;
  return Date.now() >= expiresAt;
};

/**
 * Renew the shared token pair, or reuse a token already renewed by another tab.
 *
 * The lock covers the API call and both storage writes, never a user prompt.
 * It is released before the caller handles failures or waits for acknowledgement.
 *
 * @param {string} expiredToken - Access token used by the failed resource request.
 * @param {string|null} refreshTokenAtExpiry - Refresh token captured when the
 *   401 arrived, used only if its localStorage key disappears while prompting.
 * @returns {Promise<string>} The access token to use for retrying queued requests.
 * @throws {Error} If refresh fails, returns incomplete tokens, or storage was cleared.
 */
const refreshSession = (expiredToken, refreshTokenAtExpiry) =>
  withSessionRefreshLock(async () => {
    const updatedToken = getUpdatedAccessToken(expiredToken);
    if (updatedToken) return updatedToken;

    // A different nonempty access token is already returned above. A missing
    // access token is still recoverable when a refresh token remains.
    // Prefer storage after acquiring the lock because another flow may have
    // updated the refresh token while the modal was open. The captured value
    // preserves the previous behavior when only the storage key was removed
    // after the 401 but before the user clicked Yes.
    const refreshToken =
      localStorage.getItem('refresh_token') || refreshTokenAtExpiry;
    if (!refreshToken) throw new Error('No refresh token available');

    const res = await refreshAccessToken(refreshToken);
    // This second check also protects the fallback where Web Locks is unavailable.
    // A late success must not overwrite a newer token pair in shared storage.
    const latestToken = getUpdatedAccessToken(expiredToken);
    if (latestToken) return latestToken;
    // A missing access token alone is the recoverable case. Both credentials
    // disappearing while the request was in flight indicates storage was cleared
    // by logout, so a late refresh response must not recreate that session.
    if (
      !localStorage.getItem('access_token') &&
      !localStorage.getItem('refresh_token')
    ) {
      throw new Error('Session ended during token refresh');
    }

    const { access, refresh } = readRefreshedTokens(res?.data);
    if (!access || !refresh) throw new Error('Invalid API response');

    // Write access last: other tabs use its storage event as the recovery signal,
    // so the matching rotated refresh token must already be available to them.
    localStorage.setItem('refresh_token', refresh);
    localStorage.setItem('access_token', access);
    notifySessionRefreshed();
    return access;
  });

/**
 * Release the continue-popup claim. During logout, hold it until storage is
 * cleared so another tab does not open a second dialog in that gap.
 * No-ops when the inactivity flag is off.
 * @param {boolean} loggingOut - True when this tab is about to end the session.
 * @returns {void}
 */
const finishPromptOwnership = (loggingOut) => {
  stopPromptHeartbeat();
  if (!isInactivityPromptEnabled()) return;
  if (loggingOut) holdPromptForLogout();
  else releasePromptClaim();
};

/**
 * Show the continue dialog in this tab.
 * Every tab that receives this 401 shows the same question. Yes in any tab
 * refreshes the shared token and the other tabs retry. No logs out everywhere.
 * One tab still owns the claim so a closed tab does not leave it stuck.
 * @param {string} expiredToken - Access token this attempt is replacing.
 * @returns {Promise<'confirmed'|'refreshed'|'ended'>}
 */
async function promptForContinue(expiredToken) {
  if (isInactivityPromptEnabled()) {
    if (getUpdatedAccessToken(expiredToken)) return 'refreshed';
    if (
      !localStorage.getItem('access_token') &&
      !localStorage.getItem('refresh_token')
    ) {
      return 'ended';
    }
    if (claimSessionPrompt()) startPromptHeartbeat();
  }

  const decision = await waitForSessionEvent(
    expiredToken,
    SESSION_EVENTS.TOKEN_EXPIRED,
    {
      [SESSION_EVENTS.USER_CONFIRMED]: 'confirmed',
      [SESSION_EVENTS.USER_DECLINED]: 'declined',
    }
  );

  if (getUpdatedAccessToken(expiredToken) || decision === 'refreshed') {
    return 'refreshed';
  }
  if (decision === 'declined') {
    throw new Error('User declined to refresh session');
  }
  return 'confirmed';
}

/**
 * Refresh after the user confirmed, or immediately when an active user skips the dialog.
 * A failed refresh still uses the fully-expired countdown.
 * @param {string} expiredToken - Access token this attempt is replacing.
 * @param {string|null} refreshTokenAtExpiry - Refresh token captured for this attempt.
 * @param {Error} fallbackError - Error to surface when the session cannot be renewed.
 * @param {{ silent?: boolean }} [options] - Skip toasts when the user was not asked to continue.
 * @returns {Promise<void>}
 */
async function refreshConfirmedSession(
  expiredToken,
  refreshTokenAtExpiry,
  fallbackError,
  { silent = false } = {}
) {
  if (!silent) {
    SwalToast({ icon: 'info', title: 'Refreshing your session...' });
  }
  try {
    await refreshSession(expiredToken, refreshTokenAtExpiry);
    if (!silent) {
      SwalToast({
        icon: 'success',
        title: 'Session refreshed successfully.',
      });
    }
    return;
  } catch (_refreshError) {
    if (getUpdatedAccessToken(expiredToken)) return;
  }

  await waitForSessionEvent(
    expiredToken,
    SESSION_EVENTS.SESSION_FULLY_EXPIRED,
    {
      [SESSION_EVENTS.SESSION_LOGOUT_ACKNOWLEDGED]: 'acknowledged',
    }
  );
  if (getUpdatedAccessToken(expiredToken)) return;
  throw fallbackError;
}

/**
 * Recover a resource request rejected with 401 using this origin's shared session.
 *
 * A newer token is retried immediately. Otherwise one request in this tab owns
 * the dialog/refresh flow and concurrent 401s wait in failedQueue. Both user
 * decisions and refresh failures are rechecked against storage before logout.
 *
 * @param {import('axios').AxiosError} error - First 401 for an authenticated request.
 * @returns {Promise<import('axios').AxiosResponse>} Response from the one allowed retry.
 * @throws {Error} If recovery is declined, the session expires, or the retry fails.
 */
const handleUnauthorizedError = async (error) => {
  const expiredToken = error.config._authToken;
  // Preserve the credential that was valid when expiry was detected. This is
  // intentionally local to the pending recovery flow and is never persisted.
  const refreshTokenAtExpiry = localStorage.getItem('refresh_token');
  // Mark before queueing too, otherwise a queued request's second 401 could open
  // another prompt and cause an unbounded refresh/retry loop.
  // eslint-disable-next-line no-param-reassign
  error.config._retry = true;

  if (getUpdatedAccessToken(expiredToken)) {
    // Only retry this old request. Do not close a dialog that may now concern
    // a different, newer token rejected by another request in the same tab.
    return axios(error.config);
  }

  if (isRefreshing) {
    await new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
    return axios(error.config);
  }

  isRefreshing = true;
  /**
   * Release local waiters and replay through the complete interceptor pipeline.
   * Returning the promise (without awaiting it here) keeps a resource failure
   * separate from refresh failure, so a second 401/500 cannot trigger logout.
   * @returns {Promise<import('axios').AxiosResponse>} The retried owner request.
   */
  const retryRequests = () => {
    notifySessionRefreshed();
    processQueue(null, localStorage.getItem('access_token'));
    return axios(error.config);
  };

  let loggingOut = false;
  try {
    // A dead refresh token cannot continue the session. Skip the continue
    // question and the refresh call, and show the logout countdown directly.
    const refreshTokenNow =
      localStorage.getItem('refresh_token') || refreshTokenAtExpiry;
    if (
      isRefreshTokenExpired(refreshTokenNow) &&
      !getUpdatedAccessToken(expiredToken)
    ) {
      await waitForSessionEvent(
        expiredToken,
        SESSION_EVENTS.SESSION_FULLY_EXPIRED,
        { [SESSION_EVENTS.SESSION_LOGOUT_ACKNOWLEDGED]: 'acknowledged' }
      );
      if (getUpdatedAccessToken(expiredToken)) return retryRequests();
      throw error;
    }

    // Flag off: always ask before refreshing, matching the previous 401 flow.
    // Flag on: ask when the user was idle before this request's click, or when
    // another tab is already asking. Each of those tabs shows the popup.
    // An already-active user, with no popup open elsewhere, refreshes directly.
    const inactiveAtRequest =
      error.config._idleBeforeRequest === true ||
      (error.config._idleBeforeRequest == null && isUserInactive());
    const prompt =
      !isInactivityPromptEnabled() ||
      inactiveAtRequest ||
      hasActiveForeignClaim();

    if (prompt && inactiveAtRequest) {
      clearIdleReturn();
    }

    if (prompt) {
      // Idle time was already decided from the shared clock. Do not treat this
      // 401 as a click. A background request must still open the continue dialog.
      // The refresh token may be temporarily absent while another authentication
      // flow updates shared storage, so its availability is checked only after
      // the user answers.
      const decision = await promptForContinue(expiredToken);
      if (decision === 'ended') {
        throw new Error('User declined to refresh session');
      }
      if (decision === 'refreshed' || getUpdatedAccessToken(expiredToken)) {
        return retryRequests();
      }
    }

    // refreshSession prefers the latest shared token, then falls back to the token
    // captured with this 401 if the key was removed while the modal was open.
    // An active user never sees the continue dialog, so this refresh stays quiet.
    await refreshConfirmedSession(expiredToken, refreshTokenAtExpiry, error, {
      silent: !prompt,
    });
    return retryRequests();
  } catch (sessionError) {
    // Also guard logout against an update arriving before this continuation.
    if (getUpdatedAccessToken(expiredToken)) return retryRequests();
    loggingOut = true;
    processQueue(sessionError);
    // Reserve the claim before logout clears storage. A synchronous clear must
    // not leave a gap where another tab opens a second continue popup.
    finishPromptOwnership(true);
    // Both logout implementations clear storage even when the logout API fails.
    // Preserve the original session error for callers and avoid an unhandled
    // rejection from the separate best-effort logout request.
    void logout().catch(() => {});
    throw sessionError;
  } finally {
    if (!loggingOut) finishPromptOwnership(false);
    isRefreshing = false;
  }
};

/**
 * Route refresh/logout failures to their owners and recover authenticated 401s.
 * All other failures retain the standard toast, callback and error-shaping path.
 *
 * @param {import('axios').AxiosError} error - Failure from the response interceptor.
 * @returns {Promise<import('axios').AxiosResponse>} Retry response on session recovery.
 * @throws {Error} The original auth-endpoint error or the normalized resource error.
 */
const handleErrorResponse = async (error) => {
  // Preserve Axios cancellation identity for callers (including cancelled
  // queued retries); cancellation is not an API error or a session failure.
  if (isCancel(error)) return Promise.reject(error);

  const refreshTokenPath = isCommonAuthEnabled()
    ? API_ENDPOINTS.AUTH.REFRESH_TOKEN_COMMON_AUTH
    : API_ENDPOINTS.AUTH.REFRESH_TOKEN;
  // Let the refresh owner handle failure after releasing the cross-tab lock.
  if (
    error?.config?.url ===
    `${API_ENDPOINTS.MODULE_BASE_URL.AUTH + refreshTokenPath}`
  ) {
    return Promise.reject(error);
  }

  // Auth hooks also call logout directly without _isLogoutRequest. Recognize
  // their endpoints so an expired logout never waits behind its own refresh flow.
  const isLogoutRequest =
    error?.config?._isLogoutRequest ||
    [
      API_ENDPOINTS.AUTH.LOGOUT_USER,
      API_ENDPOINTS.AUTH.LOGOUT_USER_COMMON_AUTH,
    ].some(
      (path) =>
        error?.config?.url === `${API_ENDPOINTS.MODULE_BASE_URL.AUTH}${path}`
    );
  if (isLogoutRequest && error.response?.status === 401) {
    return Promise.reject(error);
  }

  // Protected application requests can recover even if access_token was removed
  // before they were sent. Login/SSO failures and third-party 401s still reach
  // their own error handlers because they are never marked as protected here.
  if (
    error.response?.status === 401 &&
    error?.config?._isProtectedApiRequest &&
    !error?.config?._retry
  ) {
    return handleUnauthorizedError(error);
  }

  if (!error?.config?.suppressErrorToast) {
    let title;
    if (error?.response?.data) {
      try {
        if (error?.response?.data instanceof Blob) {
          const text = await error?.response?.data?.text();
          const json = JSON.parse(text);
          title = json?.msg || json?.message || error?.message;
        } else if (error?.response?.data instanceof ArrayBuffer) {
          const decoder = new TextDecoder('utf-8');
          const text = decoder.decode(error?.response?.data);
          const json = JSON.parse(text);
          title = json?.msg || error?.message;
        } else {
          title =
            error?.response?.data?.msg ||
            error?.response?.data?.message ||
            error?.message;
        }
      } catch (_parseError) {
        title = error?.message;
      }
    } else {
      title = error?.message;
    }
    SwalToast({ icon: 'error', title });
  }

  if (typeof error?.config?.errorCallback === 'function') {
    error?.config?.errorCallback(error);
  }

  const newError = new Error(error);
  newError.status = error?.status;
  newError.response = error?.response;

  return Promise.reject(newError); // NOSONAR
};

/**
 * Start the existing per-attempt APM transaction, including retried requests.
 * @param {import('axios').InternalAxiosRequestConfig} req - Outgoing request.
 * @returns {Object|null} Transaction to end on success/failure, or null without APM.
 */
const apmStartTransaction = (req) => {
  const { apm } = window;
  if (apm && req?.method && req?.url) {
    // Start a new transaction for the API request
    const transaction = apm.startTransaction(
      `API: ${req?.method} ${req?.url}`,
      'http-request',
      { managed: true, canReuse: false }
    );

    // Add custom context to the transaction
    if (transaction) {
      transaction.addLabels({
        method: req?.method,
        url: req?.url,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        profileData: localStorage.getItem('profile_data'),
        userData: localStorage.getItem('user_data'),
      });
      return transaction;
    }
    return null;
  }
  return null;
};

/**
 * Install the application's shared Axios interceptors, replacing previous ones.
 * Request retries still apply loaders, APM, encryption guards and fresh auth headers.
 * Session event listeners are owned by each pending wait and are removed on settlement.
 * @returns {void}
 */
export const initializeAxios = () => {
  startUserActivityTracking();
  const showLoaderEvent = new Event('showLoader', { bubbles: true });
  const hideLoaderEvent = new Event('hideLoader', { bubbles: true });
  let numberOfApiCallsPending = 0;
  let hideLoaderDebounce;

  /**
   * Updates the visibility of the loader based on the number of ongoing API calls.
   *
   * This function ensures that the loader is displayed whenever there are pending API calls
   * and adds a slight delay (400ms) before hiding the loader once all calls are resolved.
   * The delay helps avoid flickering effects and ensures a smooth user experience.
   *
   * @function
   * @description
   * - **Show Loader**: Immediately dispatches a `showLoaderEvent` when there are pending API calls.
   * - **Hide Loader**: Adds a 400ms delay before dispatching `hideLoaderEvent` after all API calls are resolved.
   *   This prevents flickering and ensures that the loader does not disappear prematurely in rapid API cycles.
   *
   * @example
   * // Call this function whenever an API call starts or finishes
   * updateLoaderVisibility();
   */
  const updateLoaderVisibility = () => {
    clearTimeout(hideLoaderDebounce);
    if (numberOfApiCallsPending > 0) {
      document.dispatchEvent(showLoaderEvent);
    } else {
      hideLoaderDebounce = setTimeout(() => {
        document.dispatchEvent(hideLoaderEvent);
      }, 400); // Add delay only for hiding
    }
  };

  const headersCommonOptions = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };

  axios.defaults.headers.common = headersCommonOptions;

  // Cleaning the interceptor handlers so our interceptor does not run two times.
  if (axios.interceptors.request.handlers.length > 0) {
    axios.interceptors.request.handlers = [];
  }
  if (axios.interceptors.response.handlers.length > 0) {
    axios.interceptors.response.handlers = [];
  }

  axios.interceptors.request.use((request) => {
    let req = { ...request };
    if (
      req.loader !== false &&
      req?.url !== `${window.CONFIG.VAULT_BASE_URL}/vault/model-landscape`
    ) {
      numberOfApiCallsPending += 1;
      updateLoaderVisibility();
    }
    const transaction = apmStartTransaction(req);

    req = handleRequestEncryption(req);
    const token = localStorage.getItem('access_token');
    req = attachAuthHeaders(req, token);
    req._apmTransaction = transaction;
    if (isInactivityPromptEnabled()) {
      req._idleBeforeRequest = shouldPromptBecauseIdle();
    }

    return req;
  });

  axios.interceptors.response.use(
    (response) => {
      if (response?.config?.loader !== false) {
        numberOfApiCallsPending -= 1;
        updateLoaderVisibility();
      }
      // End the transaction if it exists
      if (response.config._apmTransaction) {
        response.config._apmTransaction.end();
      }

      const isJSONResponse =
        response?.headers?.['content-type'] === 'application/json';

      /**
       * Handle decryption only when:
       * 1. encryption is enabled
       * 2. response data is in JSON format
       * 3. response data contains a key "encrypted_key" - response with status code other than 200 are not encrypted by the server. Hence this check.
       *
       * Otherwise there is no need of decryption
       */
      if (
        window.CONFIG.ENABLE_ENCRYPTION &&
        isJSONResponse &&
        Object.keys(response?.data).includes('encrypted_key')
      ) {
        const encryptedData = response?.data?.data;
        const encryptedKey = response?.data?.encrypted_key;
        const decryptedData = decryptResponseData(encryptedData, encryptedKey);
        return { ...response, data: decryptedData };
      }
      return response;
    },
    async (error) => {
      if (error?.config?.loader !== false) {
        numberOfApiCallsPending -= 1;
        updateLoaderVisibility();
      }

      // End the transaction in error case as well
      if (error.config && error.config._apmTransaction) {
        error.config._apmTransaction.end();
      }

      return handleErrorResponse(error);
    }
  );
};

export default initializeAxios;
